// src/pages/gisung_gye/GisungContracts.jsx
import React, { useState, useEffect } from "react";
import api from "../../api";
import styles from "./GisungContracts.module.css";

const API = "gisung-gye/contracts/";

const EMPTY = {
  contract_type: "구청",
  department: "",
  institution: "",
  service_name: "",
  contract_date: "",       // YYYY-MM-DD
  contract_period: "",     // 자동 생성: "YYYY.MM.DD ~ YYYY.MM.DD"
  period_start: "",        // ← 신규 (UI용)
  period_end: "",          // ← 신규 (UI용)
  start_month: "",
  start_date_text: "",     // YYYY-MM-DD
  end_date_text: "",       // YYYY-MM-DD
  contract_amount_text: "",
  contract_amount: "",
  recipient: "",
  ordering_dept: "",
  location: "",
  address: "",        


};

// YYYY-MM-DD → YYYY.MM.DD
const dash2dot = (d) => (d ? d.replace(/-/g, ".") : "");

export default function GisungContracts() {
  const [list, setList]         = useState([]);
  const [form, setForm]         = useState(EMPTY);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const fetchList = async () => {
    const params = filterType ? { type: filterType } : {};
    const res = await api.get(API, { params });
    setList(res.data);
  };

  useEffect(() => { fetchList(); }, [filterType]);

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "period_start" || name === "period_end") {
      const next = { ...form, [name]: value };
      // 두 값이 모두 있으면 contract_period 자동 합성
      if (next.period_start && next.period_end) {
        next.contract_period = `${dash2dot(next.period_start)} ~ ${dash2dot(next.period_end)}`;
      }
      setForm(next);
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  // 수정 시: contract_period → period_start / period_end 역파싱
  const parsePeriod = (periodStr) => {
    if (!periodStr) return { period_start: "", period_end: "" };
    const parts = periodStr.split("~").map((s) => s.trim().replace(/\./g, "-"));
    return { period_start: parts[0] || "", period_end: parts[1] || "" };
  };

  const onSubmit = async () => {
    setError(""); setLoading(true);
    try {
      // period_start / period_end는 백엔드 모델에 없으므로 제거 후 전송
      const { period_start, period_end, ...payload } = form;
      if (editId) {
        await api.put(`${API}${editId}/`, payload);
      } else {
        await api.post(API, payload);
      }
      setShowForm(false); setEditId(null); setForm(EMPTY);
      fetchList();
    } catch (e) {
      setError(JSON.stringify(e.response?.data || "오류 발생"));
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (item) => {
    const { period_start, period_end } = parsePeriod(item.contract_period);
    setForm({ ...item, period_start, period_end });
    setEditId(item.id);
    setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    await api.delete(`${API}${id}/`);
    fetchList();
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>📋 기성 계약 등록/관리</h2>
        <div className={styles.headerBtns}>
          {["", "구청", "공단"].map((t) => (
            <button
              key={t}
              className={`${styles.filterBtn} ${filterType === t ? styles.filterActive : ""}`}
              onClick={() => setFilterType(t)}
            >
              {t || "전체"}
            </button>
          ))}
          <button
            className={styles.addBtn}
            onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY); }}
          >
            + 계약 등록
          </button>
        </div>
      </div>

      {showForm && (
        <div className={styles.formCard}>
          <div className={styles.formTitle}>
            {editId ? "✏️ 계약 수정" : "➕ 계약 등록"}
          </div>
          <div className={styles.grid2}>
            <Field label="발주 유형" required>
              <select name="contract_type" value={form.contract_type}
                onChange={onChange} className={styles.input}>
                <option value="구청">구청 등</option>
                <option value="공단">공단</option>
              </select>
            </Field>

            <Field label="담당 부서" required>
              <input name="department" value={form.department}
                onChange={onChange} className={styles.input}
                placeholder="용역사업, 환경위생, 카페 등" />
            </Field>

            <Field label="발주처/기관명" required>
              <input name="institution" value={form.institution}
                onChange={onChange} className={styles.input} />
            </Field>

            <Field label="용역명/계약건명" required>
              <input name="service_name" value={form.service_name}
                onChange={onChange} className={styles.input} />
            </Field>

            {/* ✅ 날짜 피커 */}
            <Field label="계약연월일">
              <input name="contract_date" type="date"
                value={form.contract_date} onChange={onChange}
                className={styles.input} />
            </Field>

            {/* ✅ 계약기간: 시작 ~ 종료 날짜 피커 */}
            <Field label="계약기간">
              <div className={styles.dateRange}>
                <input name="period_start" type="date"
                  value={form.period_start} onChange={onChange}
                  className={styles.input} />
                <span className={styles.dateSep}>~</span>
                <input name="period_end" type="date"
                  value={form.period_end} onChange={onChange}
                  className={styles.input} />
              </div>
              {form.contract_period && (
                <div className={styles.periodPreview}>{form.contract_period}</div>
              )}
            </Field>

            <Field label="착수월" required>
              <input name="start_month" type="number" min={1} max={12}
                value={form.start_month} onChange={onChange}
                className={styles.input} />
            </Field>

            {/* ✅ 날짜 피커 */}
            <Field label="착수연월일">
              <input name="start_date_text" type="date"
                value={form.start_date_text} onChange={onChange}
                className={styles.input} />
            </Field>

            <Field label="계약금액(한글)" required>
              <input name="contract_amount_text" value={form.contract_amount_text}
                onChange={onChange} className={styles.input}
                placeholder="예: 금 일억원정" />
            </Field>

            <Field label="계약금액(숫자 - ,없이 작성)" required>
              <input name="contract_amount" type="number"
                value={form.contract_amount} onChange={onChange}
                className={styles.input} />
            </Field>

            {form.contract_type === "구청" && (
              <>
                {/* ✅ 날짜 피커 */}
                <Field label="완수연월일">
                  <input name="end_date_text" type="date"
                    value={form.end_date_text} onChange={onChange}
                    className={styles.input} />
                </Field>
                <Field label="귀중/귀하">
                  <input name="recipient" value={form.recipient}
                    onChange={onChange} className={styles.input}
                    placeholder="예: 금천구청장 귀하" />
                </Field>
              </>
            )}

            {form.contract_type === "공단" && (
              <>
                <Field label="발주처명">
                  <input name="ordering_dept" value={form.ordering_dept}
                    onChange={onChange} className={styles.input} />
                </Field>
                <Field label="위치">
                  <input name="location" value={form.location}
                    onChange={onChange} className={styles.input} />
                </Field>
                <Field label="주소">                                  
                  <input name="address" value={form.address}
                    onChange={onChange} className={styles.input}
                    placeholder="예: 서울 금천구 시흥대로 ..." />
                </Field>
              </>
            )}
          </div>

          {error && <div className={styles.errorBox}>{error}</div>}
          <div className={styles.formBtns}>
            <button className={styles.submitBtn} onClick={onSubmit} disabled={loading}>
              {loading ? "저장 중..." : "저장"}
            </button>
            <button className={styles.cancelBtn}
              onClick={() => { setShowForm(false); setEditId(null); }}>
              취소
            </button>
          </div>
        </div>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>

              {[
                { label: "유형" },
                { label: "부서" },
                { label: "발주처/기관명" },
                { label: "용역명" },
                { label: "계약금액" },
                { label: "착수월" },
                { label: "관리", width: 100 },   // ← 너비 고정
              ].map((h) => (
                <th key={h.label} className={styles.th}
                  style={h.width ? { width: h.width, minWidth: h.width } : {}}>
                  {h.label}
                </th>
              ))}


            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={7} className={styles.empty}>등록된 계약이 없습니다.</td></tr>
            )}
            {list.map((item) => (
              <tr key={item.id} className={styles.tr}>
                <td className={styles.td}>
                  <span className={styles.badge} style={{
                    background: item.contract_type === "구청" ? "#1565C0" : "#2E7D32"
                  }}>{item.contract_type}</span>
                </td>
                <td className={styles.td}>{item.department}</td>
                <td className={styles.td}>{item.institution}</td>
                <td className={styles.td}>{item.service_name}</td>
                <td className={`${styles.td} ${styles.tdRight}`}>
                  {Number(item.contract_amount).toLocaleString()}원
                </td>
                <td className={`${styles.td} ${styles.tdCenter}`}>{item.start_month}월</td>
                <td className={styles.td} style={{ width: 100, whiteSpace: "nowrap" }}>
                  <button className={styles.editBtn} onClick={() => onEdit(item)}>수정</button>
                  {" "}
                  <button className={styles.delBtn} onClick={() => onDelete(item.id)}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className={styles.fieldWrap}>
      <label className={styles.label}>
        {label}{required && <span className={styles.required}> *</span>}
      </label>
      {children}
    </div>
  );
}